/**
 * The default-template catalogue, drained ONCE per session and shared by every caller.
 *
 * WHY A MODULE CACHE AND NOT A REACT CONTEXT
 * ------------------------------------------
 * Four components want this exact list, and they do NOT share a subtree:
 *
 *   web/components/TemplateLibrary/useTemplateLibrary.js:72
 *   web/components/AIGenerator/AIGeneratorCard.jsx:85
 *   web/components/AIGenerator/TemplateLibraryAIView.jsx:464
 *   web/routes/index.jsx:183          ← the dashboard's OWN query
 *
 * That last one is the whole argument. It sits ABOVE `<TemplateLibrary>` in the tree, so
 * a provider mounted inside TemplateLibrary is structurally incapable of deduping it —
 * the dashboard's query has already fired by the time the provider exists. The only
 * placement that covers all four is App.jsx, and hoisting it there means draining the
 * entire library on billing, analytics, settings and every other route that never shows
 * a template. A module-level cache dedupes all four with no tree surgery and no work on
 * routes that don't ask.
 *
 * WHY A TTL IS SAFE HERE SPECIFICALLY
 * -----------------------------------
 * `isDefault: true` templates are a STATIC CATALOGUE. They change only when someone runs
 * a template import — never through a merchant action, and never as a side effect of
 * anything in the admin. So a five-minute-stale read cannot show a merchant their own
 * unreflected write, which is the failure mode that normally makes caching a fetch a bad
 * idea. Do not reuse this shape for merchant-owned models.
 *
 * The same argument is what licenses the sessionStorage layer BENEATH the module cache
 * (`storedCatalogue` / `storeCatalogue`): the module cache dies with the JS context, so a
 * full page load used to pay the whole projection before anything painted. See the JSDoc on
 * `storedCatalogue` for the layering — memory cache, then in-flight dedupe, then the stored
 * seed, which serves a first frame but never suppresses the revalidation.
 *
 * WHAT IT REPLACES
 * ----------------
 * `useAllRecords` for this one query only. That hook keys its effect on
 * `JSON.stringify(queryOptions)`, and the four call sites' selects differ by a field or
 * two (`isDefault`, `templateVersion`), so today they are four DIFFERENT keys and four
 * genuinely independent cursor drains of 1,200+ records. `CATALOGUE_VIEW` below is the
 * superset of all four selects — a couple of extra scalars on three of the call sites,
 * which is the price of one query serving everybody. useAllRecords itself is untouched;
 * it has other consumers.
 *
 * @fileoverview Shared, deduped, TTL-cached fetch of every default campaign template
 */

import { useState, useEffect } from 'react';
import { api } from '../api';

/**
 * The ONE query — a Gelly computed view, executed inline through `api.view()`.
 *
 * WHY A VIEW AND NOT `findMany`
 * -----------------------------
 * This used to cursor-walk `first: 250` in a while loop: 5 STRICTLY SEQUENTIAL round
 * trips for 1,179 records, and nothing paints until the last one lands. Measured against
 * the dev environment, back to back in one session:
 *
 *   5-trip cursor drain (n=5)   median 1,724 ms   spread 1,590–4,802   ~1 MB
 *   this projected view (n=8)   median   337 ms   spread   286–557       297 kB
 *
 * ⚠️ DO NOT ADD `settings` TO THIS VIEW. It was measured: carrying the whole blob takes
 * the median to 2,589 ms and makes it bimodal (307 ms *or* 2,551–4,287 ms) — i.e. WORSE
 * than the cursor drain it replaces. Gelly projects JSON SUB-PATHS, so the four scalars
 * anything actually reads off `settings` are pulled out flat instead. Individual
 * sub-path projections are cheap: A/B'd 4× interleaved against the two-field version on
 * 2026-08-16, medians 2,040 ms vs 1,987 ms — indistinguishable inside the noise.
 *
 *   settings.displayType             → displayType    (facets, cards, preview render mode)
 *   settings.metadata.industry       → industry       (industry tabs)
 *   settings.position                → position       (notification-bar / toast docking)
 *   settings.overlaySettings.color   → overlayColor   (preview backdrop; 2 templates)
 *
 * Every reader has been updated to prefer the flat field and keep its old nested read as
 * a fallback, so records arriving from a plain findMany/findOne still work.
 *
 * ⚠️ `displayType` is NOT optional. `templateConfig.displayType` — the fallback that
 * looks like it covers this — is present on **0 of 1,179** templates (measured), so
 * dropping the projection would render every notification bar and fullscreen template
 * as a plain popup in the library preview.
 *
 * ⚠️ THE CAMPAIGN-CREATION PATHS NEED THE REAL `settings` BLOB and can no longer take it
 * off a record from here. They fetch it by id for the ONE template the merchant clicked —
 * see `routes/templates.jsx` and `routes/index.jsx` (`fetchTemplateSettings`). If you add
 * a new "create a campaign from this template" path, do the same.
 *
 * Syntax traps live in `api/views/sidekickCampaignMetrics.gelly`'s header: the root
 * identifier is the GraphQL CONNECTION name (`campaign_templates`), there is no `in`
 * operator, and nothing may ever be string-interpolated into a body.
 *
 * `limit 3000` guards Gelly's 10,000-row truncation cap against a catalogue at 1,179.
 *
 * LIGHT SELECT: `template` / `templateConfig` are ~40 KB each and are deliberately
 * absent. Pulling them here would put ~50 MB of element trees in memory before the first
 * tile paints; the trees are fetched per visible page by `useTemplateTrees`.
 *
 * Verified by RUNNING THIS EXACT QUERY against dev 2026-08-16 (`ggt eval`, 379 ms):
 *
 *   rows 1,179 · displayType 1,179 · position 1,179 · name 1,179 · payloadUrl 1,179
 *   · isDefault==true 1,179 · industry 1,111 · overlayColor 2
 *
 * ⚠️ `industry` is populated on 1,111 of 1,179, NOT all of them — an earlier version of
 * this note claimed "present on every record" and that was wrong. The 68 nulls are FAITHFUL,
 * not a projection failure: probing five of them through `findMany({select:{settings:true}})`
 * shows `settings.metadata` absent entirely, so the old cursor drain returned nothing for
 * them either. They are the original launch seeds, and both readers
 * (`templateFacets.getTemplateIndustryKey`, `templateMetadata.getTemplateIndustry`) use
 * `record?.industry || record?.settings?.metadata?.industry`, where `||` treats the
 * projection's `null` and the old path's `undefined` identically. `overlayColor: 2` is
 * likewise real data, not a broken projection.
 *
 * ⚠️ If you re-verify this with `ggt eval`, WRITE AN EXPLICIT `return`. Its help text says
 * "the result of the last expression is printed" for multi-statement snippets; it is not —
 * `const x = 1+1; x` prints NOTHING and exits 0, which is indistinguishable from a query
 * that returned no rows. Three runs of this query were misread that way before the probe
 * itself was tested (`const x = 1+1; return x;` → `2`).
 */
const CATALOGUE_VIEW = `{
  campaign_templates {
    id
    name
    campaign_type
    previewImageURL
    payloadUrl
    templateVersion
    isDefault
    industry: settings.metadata.industry
    displayType: settings.displayType
    position: settings.position
    overlayColor: settings.overlaySettings.color
    [ where isDefault == true limit 3000 ]
  }
}`;

/** Long enough to cover a merchant's whole trip through the template flow. */
const TTL_MS = 5 * 60 * 1000;

/**
 * A 32-bit FNV-1a, in base 36. Used ONLY to version the sessionStorage key below.
 *
 * @param {string} str
 * @returns {string}
 */
function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    // The FNV prime (16777619) as shifts+adds, so this stays in 32-bit integer arithmetic
    // instead of overflowing into a float via `h * 16777619`.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

const STORAGE_PREFIX = 'ns:defaultTemplates:';

/**
 * Keyed by a hash of the VIEW ITSELF, so the key changes automatically whenever
 * `CATALOGUE_VIEW` gains, loses or renames a projection.
 *
 * That is the point: a hand-maintained `v2` suffix is one forgotten bump away from
 * deserialising a payload whose shape no longer matches what the readers expect — which
 * would not throw, it would silently render a library of records missing `displayType` or
 * `industry`. Deriving the version from the query makes that impossible to get wrong.
 */
const STORAGE_KEY = `${STORAGE_PREFIX}${hashString(CATALOGUE_VIEW)}`;

/**
 * The sessionStorage copy, read at most once per page load.
 *
 * `undefined` = not looked at yet; `null` = looked, nothing usable. The distinction keeps a
 * cold read (JSON.parse of ~300 kB) from happening once per mounting component.
 *
 * @type {Array<Object>|null|undefined}
 */
let storageSeed;

/**
 * The catalogue as of a PREVIOUS page load, if this tab still has one.
 *
 * WHY THIS EXISTS ON TOP OF THE MODULE CACHE
 * ------------------------------------------
 * The module cache dies with the JS context — i.e. on every full page load, which in an
 * embedded Shopify admin is every time the merchant re-opens the app. Gadget does not cache
 * views server-side (see `drainAll`), so each of those cold loads paid the full ~337 ms
 * projection before a single tile could paint. sessionStorage survives the reload, so the
 * library renders from the previous visit's copy on the FIRST frame while the revalidation
 * runs behind it.
 *
 * Stale data is safe here for exactly the reason the TTL is: `isDefault` templates are a
 * static catalogue that no merchant action can change (see the file header). Do not copy
 * this shape onto a merchant-owned model.
 *
 * Stored verbatim rather than trimmed — every one of the eleven projected fields has a
 * reader, and the payload measures ~297 kB against a 5 MB budget.
 *
 * @returns {Array<Object>|null} The stored catalogue, or null when absent/unusable
 */
function storedCatalogue() {
  if (storageSeed !== undefined) return storageSeed;
  storageSeed = null;

  // Wrapped whole: `sessionStorage` itself throws on access in Safari private mode and
  // under a blocked-cookies policy, before getItem is ever reached.
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const rows = JSON.parse(raw);
      // Shape check, not a schema check — the key hash already covers schema drift. This
      // only guards against a truncated or hand-edited value.
      if (Array.isArray(rows) && rows.length > 0 && rows[0]?.id) storageSeed = rows;
    }
  } catch {
    storageSeed = null;
  }

  return storageSeed;
}

/**
 * Persist a freshly drained catalogue for the next page load.
 *
 * @param {Array<Object>} records
 * @returns {void}
 */
function storeCatalogue(records) {
  if (!Array.isArray(records) || records.length === 0) return;

  try {
    const store = window.sessionStorage;

    // Sweep copies written under an older view hash. Without this a deploy that changes
    // CATALOGUE_VIEW leaves the previous ~300 kB payload sitting in the tab's quota
    // forever, unreadable and unreachable.
    for (let i = store.length - 1; i >= 0; i -= 1) {
      const key = store.key(i);
      if (key && key.startsWith(STORAGE_PREFIX) && key !== STORAGE_KEY) store.removeItem(key);
    }

    store.setItem(STORAGE_KEY, JSON.stringify(records));
    storageSeed = records;
  } catch {
    // Quota exceeded, private mode, blocked storage. The in-memory cache is unaffected and
    // this session simply loses the cross-reload head start — never a reason to fail.
  }
}

/**
 * @type {{at: number, promise: Promise<Array<Object>>|null, data: Array<Object>|null}}
 *
 * `promise` and `data` are separate slots on purpose. `promise` is the IN-FLIGHT drain
 * and exists only while one is running; `data` is the settled result and outlives it.
 * Collapsing them into one field would mean either serving a pending promise as data or
 * losing the dedupe window — the window is the entire reason this file exists.
 */
let cache = { at: 0, promise: null, data: null };

/**
 * The settled catalogue, if it is still fresh.
 *
 * @returns {Array<Object>|null} The cached records, or null when absent or expired
 */
function freshData() {
  if (!cache.data) return null;
  return Date.now() - cache.at < TTL_MS ? cache.data : null;
}

/**
 * Fetch the whole catalogue in ONE round trip.
 *
 * Grouped/collection views return an array under the root identifier. Read it
 * defensively — an empty result is `[]`, and a shape change should degrade to "no
 * templates" rather than throwing inside four unrelated components.
 *
 * The module cache above stays load-bearing: Gadget does NOT cache views server-side,
 * they recompute per request.
 *
 * @returns {Promise<Array<Object>>} Every default template
 */
async function drainAll() {
  const result = await api.view(CATALOGUE_VIEW);
  const rows = result?.campaign_templates;
  return Array.isArray(rows) ? rows : [];
}

/**
 * Get the catalogue, starting a drain only if one is neither cached nor already running.
 *
 * THE DEDUPE, precisely: `cache.promise` is assigned SYNCHRONOUSLY, in the same tick the
 * first caller enters. Every later caller in that tick — and every caller until the
 * drain settles — takes the `cache.promise` branch and awaits the SAME promise object.
 * Four mounting components therefore produce one `findMany` chain and four subscribers
 * to its result. React 18's StrictMode double-mount lands in the same branch, which is
 * why the second mount costs nothing.
 *
 * On failure the promise slot is cleared so the next mount can retry; on success it is
 * cleared too, and `data` + `at` take over as the cache.
 *
 * @returns {Promise<Array<Object>>} Every default template
 */
function loadDefaultTemplates() {
  const cached = freshData();
  if (cached) return Promise.resolve(cached);

  if (cache.promise) return cache.promise;

  const inFlight = drainAll().then(
    (records) => {
      // Guard against a `__resetDefaultTemplatesCache()` (or a newer drain) that landed
      // while this one was in the air: only the drain still owning the slot may publish.
      if (cache.promise === inFlight) {
        cache = { at: Date.now(), promise: null, data: records };
        // Same ownership guard: only the drain that still owns the slot may write through
        // to sessionStorage, so a superseded response can't become the next load's seed.
        storeCatalogue(records);
      }
      return records;
    },
    (err) => {
      if (cache.promise === inFlight) cache.promise = null;
      throw err;
    }
  );

  cache.promise = inFlight;
  return inFlight;
}

/**
 * Start the catalogue drain WITHOUT subscribing to it.
 *
 * For the `lazy()` route factory in App.jsx, which runs the moment React decides to render
 * <TemplatesPage/> — i.e. BEFORE the ~250 kB / 15-chunk templates closure has downloaded
 * and parsed. Without this the ~337 ms view request could not start until that download
 * finished, because nothing calls `useDefaultTemplates` until the route module evaluates.
 * Priming here overlaps the two. Same pattern as `primeAppHandle` (/billing) and
 * `primeFromLocation` (campaign analytics).
 *
 * ⚠️ The `.catch` goes on the DERIVED promise, never inside `loadDefaultTemplates`. It marks
 * this reference handled so a failed drain is not an unhandled rejection, while leaving the
 * shared `cache.promise` rejected — which is what the hook's own `.then(onOk, onErr)` needs
 * in order to still surface the error. Swallowing it at the source would silently blank the
 * library instead of showing an error.
 *
 * ⚠️ Never `await` this in the factory. The factory's return value is the module import;
 * awaiting the drain first would serialise exactly what this exists to parallelise.
 *
 * @returns {void}
 */
export function primeDefaultTemplates() {
  loadDefaultTemplates().catch(() => {});
}

/**
 * Every `isDefault` campaign template, shared across all call sites.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - When false, no fetch is triggered. A cache
 *   hit is still returned: the data is already in memory, so withholding it would only
 *   force the caller to render an empty state it doesn't need. `enabled` gates the
 *   NETWORK, not the data.
 * @returns {{data: Array<Object>|null, fetching: boolean, error: Error|null}}
 */
export function useDefaultTemplates({ enabled = true } = {}) {
  const [state, setState] = useState(() => {
    const cached = freshData();
    // Seeded from the cache in the initialiser rather than in the effect, so a late
    // mount paints its templates on the FIRST render instead of flashing a skeleton for
    // one frame with data that was already sitting in memory.
    //
    // Falling back to the sessionStorage copy makes the same argument across a page
    // RELOAD, where the module cache is necessarily empty. Note `fetching` still keys off
    // the in-memory cache alone: a seeded mount is `{ data, fetching: true }`, which is the
    // stale-while-revalidate state — consumers that gate on `fetching && !data` (which is
    // all of them) paint immediately and update when the drain lands.
    return { data: cached || storedCatalogue(), fetching: enabled && !cached, error: null };
  });

  useEffect(() => {
    const cached = freshData();
    if (cached) {
      // Identity-compared, not merged: the cached array is a stable reference, so this
      // is a no-op re-render on every pass after the first rather than a setState loop.
      setState((prev) => (prev.data === cached && !prev.fetching && !prev.error
        ? prev
        : { data: cached, fetching: false, error: null }));
      return undefined;
    }

    if (!enabled) {
      setState((prev) => (prev.fetching ? { ...prev, fetching: false } : prev));
      return undefined;
    }

    // Same `cancelled` discipline as useAllRecords.js:35,41,47,51 — it covers React 18
    // StrictMode's throwaway first mount and an unmount mid-drain. Note what it does NOT
    // do here: it never aborts the drain, because the drain is shared. Cancelling it on
    // one component's unmount would strand the other three subscribers.
    let cancelled = false;
    setState((prev) => (prev.fetching && !prev.error ? prev : { ...prev, fetching: true, error: null }));

    loadDefaultTemplates().then(
      (records) => {
        if (!cancelled) setState({ data: records, fetching: false, error: null });
      },
      (err) => {
        // Keep whatever is already on screen instead of blanking to null. With the
        // sessionStorage layer that is usually a slightly stale but entirely usable
        // catalogue, and throwing away a static list we already hold — in order to show an
        // error state — is strictly worse for the merchant. `error` is still reported, so a
        // consumer that wants to surface it still can.
        if (!cancelled) {
          setState((prev) => ({ data: prev.data || null, fetching: false, error: err }));
        }
      }
    );

    return () => { cancelled = true; };
  }, [enabled]);

  return state;
}

/**
 * Drop the cache. For tests only — module state survives between test cases otherwise,
 * making the second test's result depend on whether the first one ran.
 *
 * @returns {void}
 */
export function __resetDefaultTemplatesCache() {
  cache = { at: 0, promise: null, data: null };
  // The sessionStorage layer has to go too, or the "no cache" case a test just set up
  // would still be served a seed on the next mount.
  storageSeed = undefined;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // No storage in this environment (jsdom without it, node, private mode) — nothing to
    // reset, which is the state the caller wanted anyway.
  }
}

export default useDefaultTemplates;
